
import { defineComponent, reactive, toRefs, ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { formatDate, addFloat, formatThousands } from '@/utils'
import Container from '@/components/form/container/Container.vue'
import {
  ApprovalFormModel, ApprovalFormGroupModel, ApprovalFormRejectAllModel,
  ApprovalFormCancelAllModel, ApprovalFormCancelModel, ApprovalFormRejectModel
} from '@/model/process/approvalModel'
import {
  getApprovalFormDetailById, cancelSelectPayment, rejectSelectPayment,
  cancelAllPayment, rejectAllPayment, submitPayment, approved
} from '@/apis/process/approval'
import { GroupModel } from '@/components/collapse/CollapseGroupInterface'
import DynamicCollapseForApproval from '@/components/collapse/DynamicCollapseForApproval.vue'
import DynamicCollapse from '@/components/collapse/DynamicCollapse.vue'
import Decimal from 'decimal.js'
import resize from '@/layout/resize'
import { intelligentClipboard } from '@/utils/clipboard'

export default defineComponent({
  components: {
    DynamicCollapseForApproval,
    DynamicCollapse,
    Container
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const id = useRoute().params.id.toString()
    const dynamicCollapseRef = ref()
    const approvalFormKey = ref(0)
    const { isMobile } = resize()
    const reSizeBtn = computed(() => {
      return isMobile() ? 'small' : 'large'
    })
    const dialogWidth = computed(() => {
      return isMobile() ? '100%' : '80%'
    })
    const formatStatus = (status: number | null) => {
      if (status === 8) {
        return t('common.rejected')
      } else if (status === 9) {
        return t('common.canceled')
      } else if (status === 4) {
        return t('common.hasApproved')
      }
      return null
    }
    const formatPayment = (group: ApprovalFormGroupModel, paymentType: number | null) => {
      if (paymentType === 1) {
        return group.approvalPaymentSap ?? []
      } else if (paymentType === 2) {
        return group.approvalPaymentPo ?? []
      } else if (paymentType === 3) {
        return group.approvalPaymentWithoutPo ?? []
      } else if (paymentType === 4) {
        return group.approvalReimbursement ?? []
      } else if (paymentType === 5) {
        return group.approvalPaymentRefund ?? []
      }
      return []
    }
    const formatAmountName = (status: number | null) => {
      if (status && status === 4) {
        return t('bank.amountApproved')
      } else {
        return t('bank.totalAmount')
      }
    }
    const paymentSapStruct = [
      { prop: 'id', label: t('approvalForm.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'documentNumber', label: t('approvalForm.documentNumber'), width: 120 },
      { prop: 'reference', label: t('approvalForm.reference'), width: 120 },
      {
        prop: 'amount',
        label: t('approvalForm.amount'),
        width: 120,
        formatter: (row: any) => formatThousands(row.amount),
        align: 'right'
      },
      { prop: 'currency', label: t('approvalForm.currency') },
      { prop: 'dueDate', label: t('order.cutOffDate'), formatter: (row: any) => formatDate(row.dueDate, 'yyyy-MM-dd') },
      { prop: 'text', label: t('approvalForm.text') },
      {
        prop: 'paymentDate',
        label: t('approvalForm.paymentDate'),
        formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd'),
        width: 120
      },
      { prop: 'purpose', label: t('approvalForm.purpose') }
    ]
    const paymentPoStruct = [
      { prop: 'id', label: t('approvalForm.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'specialPaymentType', label: t('approvalForm.specialPaymentType'), width: 120 },
      { prop: 'reference', label: t('approvalForm.reference'), width: 120 },
      {
        prop: 'amount',
        label: t('approvalForm.amount'),
        width: 120,
        formatter: (row: any) => formatThousands(row.amount),
        align: 'right'
      },
      { prop: 'currency', label: t('approvalForm.currency') },
      {
        prop: 'paymentDate',
        label: t('approvalForm.paymentDate'),
        formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd'),
        width: 120
      },
      { prop: 'comment', label: t('approvalForm.comment') },
      { prop: 'purpose', label: t('approvalForm.purpose') },
      { prop: 'paymentDocuments', label: t('order.paymentDocuments'), slot: true, width: 120 }
    ]
    const paymentWithoutPoStruct = [
      { prop: 'id', label: t('approvalForm.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'specialPaymentType', label: t('approvalForm.specialPaymentType'), width: 120 },
      { prop: 'reference', label: t('approvalForm.reference'), width: 120 },
      {
        prop: 'amount',
        label: t('approvalForm.amount'),
        width: 120,
        formatter: (row: any) => formatThousands(row.amount),
        align: 'right'
      },
      { prop: 'currency', label: t('approvalForm.currency') },
      {
        prop: 'paymentDate',
        label: t('approvalForm.paymentDate'),
        formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd'),
        width: 120
      },
      { prop: 'comment', label: t('approvalForm.comment') },
      { prop: 'purpose', label: t('approvalForm.purpose') },
      { prop: 'paymentDocuments', label: t('order.paymentDocuments'), slot: true, width: 120 }
    ]
    const reimbursementStruct = [
      { prop: 'id', label: t('approvalForm.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      {
        prop: 'amount',
        label: t('approvalForm.amount'),
        width: 120,
        formatter: (row: any) => formatThousands(row.amount),
        align: 'right'
      },
      { prop: 'currency', label: t('approvalForm.currency') },
      {
        prop: 'paymentDate',
        label: t('approvalForm.paymentDate'),
        formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd'),
        width: 120
      },
      { prop: 'comment', label: t('approvalForm.comment') },
      { prop: 'purpose', label: t('approvalForm.purpose') }
    ]
    const paymentRefundStruct = [
      { prop: 'id', label: t('approvalForm.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'specialPaymentType', label: t('approvalForm.specialPaymentType'), width: 120 },
      { prop: 'reference', label: t('approvalForm.reference'), width: 120 },
      {
        prop: 'amount',
        label: t('approvalForm.amount'),
        width: 120,
        formatter: (row: any) => formatThousands(row.amount),
        align: 'right'
      },
      { prop: 'currency', label: t('approvalForm.currency') },
      {
        prop: 'paymentDate',
        label: t('approvalForm.paymentDate'),
        formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd'),
        width: 120
      },
      { prop: 'comment', label: t('approvalForm.comment') },
      { prop: 'purpose', label: t('approvalForm.purpose') },
      { prop: 'paymentDocuments', label: t('order.paymentDocuments'), slot: true, width: 120 }
    ]
    const dataMap = reactive({
      groupedList: Array<GroupModel>(),
      selectGroupedList: Array<any>(),
      approvalForm: {} as ApprovalFormModel | null,
      comments: '' as string,
      listLoading: true,
      dialogCancelFormVisible: false,
      dialogRejectFormVisible: false,
      dialogCancelType: 0,
      dialogRejectType: 0,
      selectable(row: any) {
        return row.status < 4
      },
      async getDetailList() {
        dataMap.listLoading = true
        const data = await getApprovalFormDetailById(id)
        if (data && data.data) {
          dataMap.approvalForm = data.data.approvalForm
          if (data.data.approvalFormGroup && data.data.approvalFormGroup.length > 0) {
            dataMap.groupedList = data.data.approvalFormGroup.map(
              (item) => ({
                key: item.code,
                name: item.name,
                accountNumber: null,
                currency: item.currency,
                paymentDate: item.paymentDate ? formatDate(item.paymentDate, 'yyyy-MM-dd') : null,
                totalAmount: new Decimal(item.totalAmount || 0).toString(),
                totalAmountName: formatAmountName(dataMap.approvalForm?.status ?? null),
                tableList: formatPayment(item, dataMap.approvalForm?.paymentType ?? null)
              })
            )
          }
        } else {
          dataMap.groupedList = []
        }
        approvalFormKey.value += 1
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      showDialogCancelSelect() {
        if (dynamicCollapseRef.value && dynamicCollapseRef.value.getSelectionRows().totalCount > 0) {
          dataMap.dialogCancelType = 0
          dataMap.dialogCancelFormVisible = true
          return
        }
        ElMessage({
          message: t('order.selectTip'),
          type: 'error'
        })
      },
      showDialogCancelAll() {
        dataMap.dialogCancelType = 1
        dataMap.dialogCancelFormVisible = true
      },
      showDialogRejectSelect() {
        dataMap.selectGroupedList = []
        if (dynamicCollapseRef.value) {
          const selectionRows = dynamicCollapseRef.value.getSelectionRows()
          if (selectionRows.totalCount > 0) {
            const { byGroup } = selectionRows
            dataMap.selectGroupedList = dataMap.groupedList.map((group, groupIndex) => {
              if (!byGroup[groupIndex]) {
                return { ...group, tableList: [] }
              }
              // 被选中的组
              const selectIds = new Set(byGroup[groupIndex].map((r: any) => r.id))
              // 新 tableList
              const newTableList = group.tableList.filter(row => selectIds.has(row.id))
              // 重新计算 totalAmount
              const newTotalAmount = newTableList.reduce((sum, r) => addFloat(sum, r.amount), 0)
              return {
                ...group,
                tableList: newTableList,
                totalAmount: newTotalAmount
              }
            }).filter(group => group.tableList.length > 0)
            dataMap.dialogRejectType = 0
            dataMap.dialogRejectFormVisible = true
            return
          }
        }
        ElMessage({
          message: t('order.selectTip'),
          type: 'error'
        })
      },
      showDialogRejectAll() {
        dataMap.selectGroupedList = dataMap.groupedList.map((group) => {
          // 新 tableList
          const newTableList = group.tableList.filter(row => row.status >= 0 && row.status < 4)
          // 重新计算 totalAmount
          const newTotalAmount = newTableList.reduce((sum, r) => addFloat(sum, r.amount), 0)
          return {
            ...group,
            tableList: newTableList,
            totalAmount: newTotalAmount
          }
        }).filter(group => group.tableList.length > 0)
        // dataMap.selectGroupedList = Object.assign([], dataMap.groupedList)
        dataMap.dialogRejectType = 1
        dataMap.dialogRejectFormVisible = true
      },
      async handleClickLink(row: any, event: MouseEvent) {
        await intelligentClipboard(row.paymentDocuments, event)
      },
      async handleReturnClick() {
        if (isMobile()) {
          await router.push({ path: '/approvalForm/list' })
        }
      }
    })

    const showSubmit = (approvalForm: ApprovalFormModel | null) => {
      return approvalForm && approvalForm.status !== null && approvalForm?.status === 0
    }

    const showApproved = (approvalForm: ApprovalFormModel | null) => {
      return approvalForm && approvalForm.status !== null && approvalForm?.status < 4 && approvalForm?.status > 0
    }

    const isHistory = (approvalForm: ApprovalFormModel | null) => {
      return approvalForm && approvalForm.status !== null && approvalForm?.status > 3
    }

    const formatApprover = (approvers: string | null) => {
      if (approvers != null && approvers.length > 0) {
        return approvers.replaceAll(',', ' / ')
      }
      return null
    }

    const getApprover = (row: any, formApprover: string | null) => {
      if (row.status !== null) {
        switch (row.status) {
          case 0:
          case 1:
          case 2:
          case 3:
            if (formApprover !== null && formApprover.length > 0) {
              return formatApprover(formApprover)
            }
            break
          case 4:
          case 8:
          case 9:
            if (row.approver !== null && row.approver.length > 0) {
              return row.approver
            }
            break
          default:
            break
        }
      }
      return ''
    }

    const getColumnStruct = (approvalForm: ApprovalFormModel | null, showStatus: boolean | null) => {
      let columnStruct: any[] = []
      if (approvalForm) {
        switch (approvalForm.paymentType) {
          case 1:
            columnStruct = Object.assign([], paymentSapStruct)
            break
          case 2:
            columnStruct = Object.assign([], paymentPoStruct)
            break
          case 3:
            columnStruct = Object.assign([], paymentWithoutPoStruct)
            break
          case 4:
            columnStruct = Object.assign([], reimbursementStruct)
            break
          case 5:
            columnStruct = Object.assign([], paymentRefundStruct)
            break
        }
        if (showStatus && approvalForm.status !== null && approvalForm?.status > 0) {
          columnStruct.push({
            prop: 'approver',
            label: isHistory(approvalForm) ? t('approvalForm.finalApprover') : t('approvalForm.currentApprover'),
            formatter: (row: any) => getApprover(row, approvalForm.approver),
            width: 120
          })
          columnStruct.push({
            prop: 'status',
            label: t('table.status'),
            formatter: (row: any) => formatStatus(row.status),
            icon: 'warning',
            showIcon: (row: any) => row.status === 8,
            iconTooltip: 'comments'
          })
        }
      }
      return columnStruct
    }

    const handleCancelSelect = async() => {
      if (dynamicCollapseRef.value) {
        const selectionRows = dynamicCollapseRef.value.getSelectionRows()
        if (selectionRows.totalCount > 0) {
          const { allRows } = selectionRows

          const params: ApprovalFormCancelModel = {
            idList: allRows.map((r: any) => r.id)
          }
          const res = await cancelSelectPayment(params)
          if (res?.data && res?.data > 0) {
            ElMessage({
              message: t('common.operateSuccess'),
              type: 'success',
              duration: 2000
            })
            // 当前审批单状态已经是取消或驳回
            dataMap.dialogCancelFormVisible = false
            if (dataMap.approvalForm && dataMap.approvalForm.status !== null && dataMap.approvalForm.status > 4 && !isMobile()) {
              router.back()
            } else {
              window.location.reload()
            }
          } else {
            ElMessage({
              message: res?.msg,
              type: 'error',
              duration: 2000
            })
          }
          return
        }
      }
      ElMessage({
        message: t('order.selectTip'),
        type: 'error'
      })
    }

    const handleRejectSelect = async() => {
      if (dynamicCollapseRef.value) {
        const selectionRows = dynamicCollapseRef.value.getSelectionRows()
        if (selectionRows.totalCount > 0) {
          const { allRows } = selectionRows

          const params: ApprovalFormRejectModel = {
            idList: allRows.map((r: any) => r.id),
            comments: dataMap.comments
          }
          const res = await rejectSelectPayment(params)
          if (res?.data && res?.data > 0) {
            ElMessage({
              message: t('common.operateSuccess'),
              type: 'success',
              duration: 2000
            })
            await dataMap.getDetailList()
            dataMap.dialogRejectFormVisible = false
            // 当前审批单状态已经是取消或驳回
            if (dataMap.approvalForm && dataMap.approvalForm.status !== null && dataMap.approvalForm.status > 4 && !isMobile()) {
              router.back()
            } else {
              window.location.reload()
            }
          } else {
            ElMessage({
              message: res?.msg,
              type: 'error',
              duration: 2000
            })
          }
          return
        }
      }
      ElMessage({
        message: t('order.selectTip'),
        type: 'error'
      })
    }

    const handleCancelAll = async() => {
      if (dataMap.approvalForm) {
        const params: ApprovalFormCancelAllModel = {
          approvalFormId: dataMap.approvalForm.id
        }
        const res = await cancelAllPayment(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.operateSuccess'),
            type: 'success',
            duration: 2000
          })
          await dataMap.getDetailList()
          dataMap.dialogCancelFormVisible = false
          // 当前审批单状态已经是取消或驳回
          if (dataMap.approvalForm && dataMap.approvalForm.status !== null && dataMap.approvalForm.status > 4 && !isMobile()) {
            router.back()
          } else {
            window.location.reload()
          }
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      }
    }

    const handleRejectAll = async() => {
      if (dataMap.approvalForm) {
        const params: ApprovalFormRejectAllModel = {
          approvalFormId: dataMap.approvalForm.id,
          comments: dataMap.comments
        }
        const res = await rejectAllPayment(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.operateSuccess'),
            type: 'success',
            duration: 2000
          })
          await dataMap.getDetailList()
          dataMap.dialogRejectFormVisible = false
          // 当前审批单状态已经是取消或驳回
          if (dataMap.approvalForm && dataMap.approvalForm.status !== null && dataMap.approvalForm.status > 4 && !isMobile()) {
            router.back()
          } else {
            window.location.reload()
          }
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      }
    }

    const handleSubmit = async() => {
      if (dataMap.approvalForm) {
        const res = await submitPayment(dataMap.approvalForm.id)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.operateSuccess'),
            type: 'success',
            duration: 2000
          })
          // await dataMap.getDetailList()
          await router.push({ path: '/approvalForm/list', state: { refresh: true } })
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      }
    }

    const handleApproved = async() => {
      if (dataMap.approvalForm) {
        const res = await approved(dataMap.approvalForm.id)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.operateSuccess'),
            type: 'success',
            duration: 2000
          })
          // await dataMap.getDetailList()
          if (!isMobile()) {
            await router.push({ path: '/approvalForm/list', state: { refresh: true } })
          } else {
            window.location.reload()
          }
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      }
    }

    onMounted(() => {
      dataMap.getDetailList()
    })
    return {
      t,
      dynamicCollapseRef,
      approvalFormKey,
      reSizeBtn,
      dialogWidth,
      isMobile,
      showSubmit,
      showApproved,
      getColumnStruct,
      handleCancelSelect,
      handleRejectSelect,
      handleCancelAll,
      handleRejectAll,
      handleSubmit,
      handleApproved,
      ...toRefs(dataMap)
    }
  }
})
